import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Languages, Award, ShieldCheck, CheckCircle2, CalendarCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PageHero } from "@/components/site/PageHero";
import { CTASection } from "@/components/site/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { IMAGES } from "@/lib/assets";

const arr = (t, k) => {
  const v = t(k, { returnObjects: true });
  return Array.isArray(v) ? v : [];
};

export default function About() {
  const { t } = useTranslation();
  const d = (k, def) => t(`doctor.${k}`, def);
  const stages = arr(t, "doctor.stages");
  const timeline = arr(t, "doctor.timeline");
  const whyMatters = arr(t, "doctor.whyMatters");
  const principles = arr(t, "doctor.principles");
  const credentials = arr(t, "doctor.credentials");
  const founder = arr(t, "doctor.founderText");
  const trustStatements = arr(t, "doctor.trustStatements");
  const faq = arr(t, "doctor.faq");
  const badges = arr(t, "doctor.languageBadges");
  const portraitBody = arr(t, "doctor.portrait.body");
  const heroIntro = arr(t, "doctor.hero.intro");

  return (
    <div data-testid="about-page">
      <PageHero
        kicker={d("hero.eyebrow")}
        title={d("name")}
        text={d("hero.headline")}
        image={IMAGES.doctor}
      />

      {/* Opening portrait */}
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-sm font-medium uppercase tracking-editorial text-champagne-gold">{d("role")}</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-4 font-serif text-3xl leading-snug text-deep-ink lg:text-4xl">
                  {d("portrait.headline")}
                </h2>
              </Reveal>
              <div className="mt-6 space-y-5">
                {heroIntro.map((p, i) => (
                  <Reveal key={`hi-${i}`} delay={0.04 * i}>
                    <p className="text-lg leading-relaxed text-deep-ink/80">{p}</p>
                  </Reveal>
                ))}
                {portraitBody.map((p, i) => (
                  <Reveal key={`pb-${i}`} delay={0.03 * i}>
                    <p className="text-base leading-relaxed text-deep-ink/75">{p}</p>
                  </Reveal>
                ))}
              </div>
              <Reveal>
                <blockquote className="mt-8 border-s-2 border-champagne-gold ps-6 font-serif text-2xl leading-snug text-deep-ink">
                  {d("portrait.highlight")}
                </blockquote>
              </Reveal>
            </div>
            {/* Trust rail */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-28">
                <Reveal delay={0.1}>
                  <div className="rounded-3xl bg-sand p-8" data-testid="doctor-trust-card">
                    <p className="text-xs font-medium uppercase tracking-editorial text-muted-sage">
                      {d("hero.trustLine")}
                    </p>
                    <div className="mt-6 space-y-3">
                      {trustStatements.map((s, i) => (
                        <div key={i} className="flex gap-3 text-sm leading-relaxed text-deep-ink/80">
                          <ShieldCheck strokeWidth={1.5} className="mt-0.5 h-4 w-4 flex-shrink-0 text-champagne-gold" />
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to="/book"
                      data-testid="about-book-cta"
                      className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-deep-ink px-6 py-4 text-sm font-medium text-warm-ivory transition-colors duration-300 hover:bg-champagne-gold hover:text-deep-ink"
                    >
                      <CalendarCheck className="h-4 w-4" />
                      {t("nav.book", "Book a consultation")}
                    </Link>
                  </div>
                </Reveal>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Professional journey */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl text-deep-ink lg:text-4xl">{d("journeyTitle")}</h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-deep-ink/70">{d("journeyIntro")}</p>
          </Reveal>
          <div className="mt-10">
            <Reveal delay={0.05}>
              <Accordion type="single" collapsible defaultValue="j-0" className="w-full">
                {stages.map((s, i) => (
                  <AccordionItem key={i} value={`j-${i}`} data-testid={`stage-${i}`} className="border-b border-border">
                    <AccordionTrigger className="py-6 text-start hover:no-underline">
                      <span className="flex flex-col gap-1 pe-4">
                        <span className="text-xs font-medium uppercase tracking-editorial text-champagne-gold">{s.eyebrow}</span>
                        <span className="font-serif text-xl leading-snug text-deep-ink lg:text-2xl">{s.headline}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <div className="space-y-4">
                        {(s.body || []).map((p, bi) => (
                          <p key={bi} className="text-base leading-relaxed text-deep-ink/75">{p}</p>
                        ))}
                      </div>
                      {(s.bullets || []).length > 0 && (
                        <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                          {s.bullets.map((b, li) => (
                            <li key={li} className="flex gap-2.5 text-sm leading-relaxed text-deep-ink/80">
                              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-champagne-gold" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Egypt relocation */}
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <h2 className="font-serif text-3xl leading-snug text-deep-ink lg:text-4xl">{d("egypt.headline")}</h2>
            </Reveal>
            <div className="mt-6 space-y-5">
              {arr(t, "doctor.egypt.body").map((p, i) => (
                <Reveal key={i} delay={0.03 * i}>
                  <p className="text-base leading-relaxed text-deep-ink/80">{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <blockquote className="mt-8 border-s-2 border-champagne-gold ps-6 font-serif text-xl leading-snug text-deep-ink">
                {d("egypt.highlight")}
              </blockquote>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl leading-snug text-deep-ink lg:text-4xl">{d("timelineTitle")}</h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-deep-ink/70">{d("timelineIntro")}</p>
          </Reveal>
          <div className="relative mt-12 ps-8">
            <span className="absolute inset-y-0 start-0 w-px bg-champagne-gold/40" />
            <div className="space-y-10">
              {timeline.map((e, i) => (
                <Reveal key={i} delay={0.02}>
                  <div className="relative" data-testid={`timeline-${i}`}>
                    <span className="absolute -start-8 top-1.5 flex h-3 w-3 -translate-x-1/2 items-center justify-center rounded-full bg-champagne-gold" style={{ insetInlineStart: "-2rem" }} />
                    {e.period && (
                      <p className="text-sm font-medium uppercase tracking-editorial text-champagne-gold">{e.period}</p>
                    )}
                    <h3 className="mt-1 font-serif text-xl text-deep-ink">{e.place}</h3>
                    <p className="mt-2 max-w-2xl text-base leading-relaxed text-deep-ink/75">{e.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why this international path matters */}
      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl leading-snug text-deep-ink lg:text-4xl">{d("whyMattersTitle")}</h2>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-deep-ink/70">{d("whyMattersIntro")}</p>
          </Reveal>
          <div className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
            {whyMatters.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.05}>
                <div className="border-t border-border pt-6">
                  <h3 className="font-serif text-xl text-deep-ink">{p.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-deep-ink/70">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder story */}
      <section className="bg-deep-ink">
        <div className="mx-auto max-w-4xl px-5 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <h2 className="font-serif text-3xl leading-snug text-warm-ivory lg:text-4xl">{d("founderTitle")}</h2>
          </Reveal>
          <div className="mt-8 space-y-6">
            {founder.map((p, i) => (
              <Reveal key={i} delay={0.02}>
                <p className="text-lg leading-relaxed text-warm-ivory/85">{p}</p>
              </Reveal>
            ))}
            <Reveal>
              <p className="pt-2 font-serif text-xl text-champagne-gold">{d("name")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl text-deep-ink lg:text-4xl">{d("principlesTitle")}</h2>
          </Reveal>
          <div className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
            {principles.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.05}>
                <div className="border-t border-border pt-6">
                  <h3 className="font-serif text-2xl text-deep-ink">{p.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-deep-ink/70">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials + Languages */}
      <section className="bg-warm-ivory">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-20 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <div className="h-full rounded-3xl border border-border bg-warm-ivory p-8 lg:p-10">
              <Award className="h-6 w-6 text-champagne-gold" strokeWidth={1.6} />
              <h3 className="mt-4 font-serif text-2xl text-deep-ink">{d("credentialsTitle")}</h3>
              <ul className="mt-6 space-y-3">
                {credentials.map((c, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-deep-ink/80">
                    <CheckCircle2 strokeWidth={1.5} className="mt-0.5 h-4 w-4 flex-shrink-0 text-champagne-gold" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 rounded-xl bg-sand px-4 py-3 text-xs leading-relaxed text-deep-ink/60">
                {d("credentialsNote")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="flex h-full flex-col rounded-3xl bg-sand p-8 lg:p-10">
              <Languages className="h-6 w-6 text-champagne-gold" strokeWidth={1.6} />
              <h3 className="mt-4 font-serif text-2xl text-deep-ink">{d("languagesTitle")}</h3>
              <p className="mt-3 text-base leading-relaxed text-deep-ink/70">{d("languagesText")}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {badges.map((b, i) => (
                  <span key={i} className="rounded-full border border-champagne-gold/40 px-4 py-1.5 text-sm text-deep-ink">
                    {b}
                  </span>
                ))}
              </div>
              <p className="mt-auto pt-6 font-serif text-lg leading-snug text-deep-ink">{d("languagesSupporting")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand">
        <div className="mx-auto max-w-4xl px-5 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl text-deep-ink lg:text-4xl">{d("faqTitle")}</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-8 w-full">
              {faq.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} data-testid={`doctor-faq-${i}`} className="border-b border-deep-ink/10">
                  <AccordionTrigger className="py-5 text-start font-serif text-lg text-deep-ink hover:no-underline lg:text-xl">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-deep-ink/75">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
